import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { invoicesApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';

const SendWhatsappDialog = ({ open, onClose, invoice, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    if (open && invoice?.id) {
      loadPreview();
    } else {
      setPreview(null);
      setError(null);
      setPhoneNumber('');
      setMessageText('');
    }
  }, [open, invoice?.id]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await invoicesApi.getWhatsappPreview(invoice.id);
      const data = response.data?.data;

      if (!data) {
        throw new Error('Dati preview non validi');
      }

      setPreview(data);
      setPhoneNumber(data.phone_number || '');
      setMessageText(data.message_text || '');
    } catch (err) {
      console.error('Errore caricamento preview WhatsApp:', err);
      setError(err.response?.data?.message || 'Errore nel caricamento della preview WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleAttachmentAction = async (attachment, mode = 'download') => {
    try {
      const response = await invoicesApi.downloadAttachment(invoice.id, attachment.type);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      if (mode === 'preview') {
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Errore download allegato WhatsApp:', err);
      setError('Errore nel download dell\'allegato');
    }
  };

  const sendMutation = useMutation({
    mutationFn: async () => {
      return invoicesApi.sendWhatsapp(invoice.id, {
        phone_number: phoneNumber,
        message_text: messageText,
      });
    },
    onSuccess: (response) => {
      if (onSuccess) onSuccess(response?.data);
      onClose();
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Errore durante l\'invio WhatsApp');
    },
  });

  const canSend = useMemo(() => {
    return Boolean(preview?.whatsapp_ready && preview?.has_customer && phoneNumber.trim());
  }, [phoneNumber, preview?.has_customer, preview?.whatsapp_ready]);

  const statusMessage = useMemo(() => {
    if (!preview) return null;
    if (!preview.has_customer) return preview.exclusion_reason || 'Cliente non associato alla fattura';
    if (!preview.whatsapp_ready) return preview.exclusion_reason || 'WhatsApp non connesso';
    if (!phoneNumber.trim()) return 'Numero WhatsApp destinatario obbligatorio';
    return null;
  }, [phoneNumber, preview]);

  const handleSend = () => {
    if (!canSend) {
      setError(statusMessage || 'Impossibile inviare via WhatsApp');
      return;
    }

    sendMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={sendMutation.isPending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {parseFloat(invoice?.f_totale || 0) < 0 ? 'Invia Nota di Credito via WhatsApp' : 'Invia Fattura via WhatsApp'}
          </Typography>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon />} disabled={sendMutation.isPending}>
            Chiudi
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" icon={<WhatsAppIcon />}>
              <Typography variant="body2">
                <strong>{parseFloat(invoice?.f_totale || 0) < 0 ? 'Nota di Credito:' : 'Fattura:'}</strong> {formatNuf(invoice?.sezionale, invoice?.nuf) || '-'} |
                <strong> Cliente:</strong> {preview?.customer_name || '-'}
              </Typography>
            </Alert>

            {statusMessage && (
              <Alert severity="warning">{statusMessage}</Alert>
            )}

            <TextField
              label="Numero WhatsApp"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
              required
              type="tel"
              helperText="Numero destinatario modificabile prima dell'invio"
            />

            {preview?.attachments?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Allegati ({preview.attachments.length})
                </Typography>
                <List dense>
                  {preview.attachments.map((attachment, index) => (
                    <ListItem
                      key={`${attachment.type}-${index}`}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleAttachmentAction(attachment, 'preview')}
                          >
                            Anteprima
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleAttachmentAction(attachment, 'download')}
                          >
                            Scarica
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <AttachFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={attachment.filename || `Allegato ${index + 1}`}
                        secondary={attachment.description || 'Documento PDF'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Anteprima Messaggio
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.paper',
                  minHeight: 140,
                  whiteSpace: 'pre-wrap',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {messageText || 'Nessun messaggio di testo. Verranno inviati solo gli allegati.'}
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Messaggio WhatsApp"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              multiline
              rows={6}
              fullWidth
              helperText="Puoi modificare il testo prima di inviare la fattura al cliente"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sendMutation.isPending}>
          Annulla
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={sendMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSend}
          disabled={loading || sendMutation.isPending || !canSend}
        >
          {sendMutation.isPending ? 'Invio...' : 'Invia WhatsApp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendWhatsappDialog;